import { rectContains, type Point, type Rect } from "../../common/geometry.js";
import { Container, type ContainerOptions } from "../core/Container.js";
import type { MouseEventArgs } from "../core/events.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import type { Chart } from "./Chart.js";
import { ChartLink, type ChartLinkOptions } from "./ChartLink.js";

export interface ChartPaneOptions {
  /** This pane's share of the height, against the others'; 1 when absent. */
  weight?: number;
  /** The least it may be dragged down to, in px; 48 when absent. */
  minHeight?: number;
}

export interface ChartPanesOptions extends ContainerOptions {
  /** The panes, top to bottom, each a chart or a chart with its options. */
  panes?: readonly (Chart | ({ chart: Chart } & ChartPaneOptions))[];
  /** The gap between panes, in px, which is the divider dragged to resize them; 6 when absent. */
  gap?: number;
  /** What the panes share; everything, by default. */
  link?: ChartLinkOptions;
}

interface Pane {
  chart: Chart;
  weight: number;
  minHeight: number;
}

/**
 * Charts stacked in panes over one x axis - price, then volume, then an RSI or a MACD - the way a
 * trading screen lays them out.
 *
 * The panes share a `ChartLink`, so they zoom, pan and hover together and their plots line up; only
 * the bottom pane shows the x axis' labels, since the others would repeat them. Heights are shared
 * out by weight, and the gap between two panes is a divider that resizes them, by mouse or finger.
 * A hidden pane gives its room to the rest, and the bottom visible pane takes over the labels.
 *
 * ```ts
 * const panes = new ChartPanes({ dock: "fill", panes: [{ chart: price, weight: 3 }, volume, strength] });
 * ```
 */
export class ChartPanes extends Container {
  readonly link: ChartLink;
  #panes: Pane[] = [];
  #gap: number;
  #drag: { index: number; offset: number } | null = null;
  #hoveredDivider = -1;
  #onVisible = () => {
    this.#syncAxes();
    this.performLayout();
  };

  constructor(options: ChartPanesOptions = {}) {
    super({ width: 600, height: 420, ...options });
    this.#gap = options.gap ?? 6;
    this.link = new ChartLink([], options.link);
    for (const p of options.panes ?? []) {
      if ("chart" in p) this.addPane(p.chart, p);
      else this.addPane(p);
    }
    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerup", () => this.#endDrag());
    this.on("pointerleave", () => this.#setHoveredDivider(-1));
  }

  /** The charts, top to bottom. */
  get panes(): readonly Chart[] {
    return this.#panes.map((p) => p.chart);
  }

  get gap(): number {
    return this.#gap;
  }
  set gap(v: number) {
    this.#gap = Math.max(0, v);
    this.performLayout();
    this.invalidate();
  }

  /** Add a chart as the bottom pane, or at `index`. */
  addPane(chart: Chart, options: ChartPaneOptions = {}, index = this.#panes.length): Chart {
    if (this.#panes.some((p) => p.chart === chart)) return chart;
    const pane = { chart, weight: Math.max(0.01, options.weight ?? 1), minHeight: Math.max(0, options.minHeight ?? 48) };
    this.#panes.splice(Math.max(0, Math.min(index, this.#panes.length)), 0, pane);
    this.controls.add(chart);
    this.link.add(chart);
    chart.on("visiblechanged", this.#onVisible);
    this.#syncAxes();
    this.performLayout();
    return chart;
  }

  removePane(chart: Chart): boolean {
    const i = this.#panes.findIndex((p) => p.chart === chart);
    if (i < 0) return false;
    this.#panes.splice(i, 1);
    chart.off("visiblechanged", this.#onVisible);
    this.link.remove(chart);
    this.controls.remove(chart);
    chart.showXAxis = true;
    this.#syncAxes();
    this.performLayout();
    return true;
  }

  /** A pane's share of the height. */
  weightOf(chart: Chart): number {
    return this.#panes.find((p) => p.chart === chart)?.weight ?? 0;
  }

  setWeight(chart: Chart, weight: number): void {
    const pane = this.#panes.find((p) => p.chart === chart);
    if (!pane) return;
    pane.weight = Math.max(0.01, weight);
    this.performLayout();
  }

  /** Only the bottom visible pane shows the x axis' labels. */
  #syncAxes(): void {
    const visible = this.#panes.filter((p) => p.chart.visible);
    const last = visible[visible.length - 1];
    for (const p of this.#panes) p.chart.showXAxis = p === last;
  }

  // --- layout -------------------------------------------------------------

  /** The panes place their charts; nothing here is anchored. */
  protected override applyAnchors(): void {}

  protected override onResize(): void {
    this.performLayout();
  }

  protected override performLayout(): void {
    // Container's constructor reaches here before the panes exist.
    if (!this.#panes) return;
    const { width, height } = this.clientSize;
    const visible = this.#panes.filter((p) => p.chart.visible);
    if (visible.length === 0) return;
    const room = Math.max(0, height - this.#gap * (visible.length - 1));
    const total = visible.reduce((s, p) => s + p.weight, 0);
    let y = 0;
    visible.forEach((p, i) => {
      const last = i === visible.length - 1;
      const h = last ? Math.max(0, height - y) : Math.round((room * p.weight) / total);
      p.chart.setBounds(0, y, width, h);
      y += h + this.#gap;
    });
    this.invalidate();
  }

  /** The divider under each visible pane but the last, with the indices of the panes either side. */
  #dividers(): { rect: Rect; above: Pane; below: Pane }[] {
    const visible = this.#panes.filter((p) => p.chart.visible);
    const out: { rect: Rect; above: Pane; below: Pane }[] = [];
    for (let i = 0; i < visible.length - 1; i++) {
      const above = visible[i]!;
      const bottom = above.chart.y + above.chart.height;
      out.push({ rect: { x: 0, y: bottom, width: this.clientSize.width, height: this.#gap }, above, below: visible[i + 1]! });
    }
    return out;
  }

  #dividerAt(p: Point): number {
    return this.#dividers().findIndex((d) => rectContains({ ...d.rect, y: d.rect.y - 2, height: d.rect.height + 4 }, p));
  }

  // --- input --------------------------------------------------------------

  override cursorAt(p: Point): string {
    if (this.#drag || this.#dividerAt(p) >= 0) return "row-resize";
    return super.cursorAt(p);
  }

  /** A drag on a divider resizes; a finger there does not scroll whatever is outside. */
  override handlesDragAt(p: Point): boolean {
    return this.#dividerAt(p) >= 0;
  }

  #setHoveredDivider(i: number): void {
    if (this.#hoveredDivider === i) return;
    this.#hoveredDivider = i;
    this.invalidate();
  }

  #onPointerDown(e: MouseEventArgs): void {
    if (e.button !== 0) return;
    const i = this.#dividerAt({ x: e.x, y: e.y });
    if (i < 0) return;
    const d = this.#dividers()[i]!;
    this.#drag = { index: i, offset: e.y - d.rect.y };
    this.desktop?.input.setCapture(this);
    e.handled = true;
    this.invalidate();
  }

  #onPointerMove(e: MouseEventArgs): void {
    if (!this.#drag) {
      this.#setHoveredDivider(this.#dividerAt({ x: e.x, y: e.y }));
      return;
    }
    const d = this.#dividers()[this.#drag.index];
    if (!d) return;
    // Move the divider: the pane above takes what the pane below gives, each kept to its minimum,
    // and their weights are rewritten to the heights they now have, so a resize of the whole keeps
    // the proportion the drag left.
    const top = d.above.chart.y;
    const pair = d.above.chart.height + d.below.chart.height;
    const above = Math.min(Math.max(e.y - this.#drag.offset - top, d.above.minHeight), pair - d.below.minHeight);
    const weights = d.above.weight + d.below.weight;
    d.above.weight = Math.max(0.01, (weights * above) / pair);
    d.below.weight = Math.max(0.01, weights - d.above.weight);
    this.performLayout();
    e.handled = true;
  }

  #endDrag(): void {
    if (!this.#drag) return;
    this.#drag = null;
    this.desktop?.input.releaseCapture(this);
    this.invalidate();
  }

  // --- paint --------------------------------------------------------------

  /** The panes paint themselves; the dividers go on top, drawn by the theme as splitters. */
  protected override onPaint(): void {}

  protected override onPaintOverlay(rc: RenderContext, theme: Theme): void {
    this.#dividers().forEach((d, i) => {
      if (d.rect.height <= 0) return;
      const dragging = this.#drag?.index === i;
      theme.drawSplitter(rc, d.rect, {
        hovered: this.#hoveredDivider === i,
        pressed: dragging,
        dragging,
        focused: false,
        disabled: !this.effectivelyEnabled,
        vertical: false,
      });
    });
  }
}
